import { reportsApiExportRequest, type ReportExportFile } from './apiClient';
import type { ApiResult } from './types';
import { toIsoDateEnd, toIsoDateStart } from '../utils/reportDates';

export type { ReportExportFile };

export type ReportSearchPage<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

type ApiSearchPage<T> = {
  content?: T[];
  page?: number;
  number?: number;
  size?: number;
  total_elements?: number;
  totalElements?: number;
  total_pages?: number;
  totalPages?: number;
};

export function mapReportSearchPage<T>(payload: ApiSearchPage<T> | undefined, page: number, size: number): ReportSearchPage<T> {
  const content = payload?.content ?? [];
  const totalElements = payload?.total_elements ?? payload?.totalElements ?? content.length;
  const totalPages = payload?.total_pages ?? payload?.totalPages ?? (content.length ? 1 : 0);
  return {
    content,
    page: payload?.page ?? payload?.number ?? page,
    size: payload?.size ?? size,
    totalElements,
    totalPages: Math.max(totalPages, content.length ? 1 : 0),
  };
}

/** Unwrap Spring-style report search pages (`{ content, totalPages }` inside envelope `data`). */
export function extractReportSearchPagePayload<T>(payload: unknown): ApiSearchPage<T> | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.content)) return record as ApiSearchPage<T>;
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return record.data as ApiSearchPage<T>;
  }
  return undefined;
}

export function nullIfEmpty<T>(value: readonly T[] | undefined | null): T[] | null {
  if (!value || value.length === 0) return null;
  return [...value];
}

export function nullIfEmptyNumericIds(ids: readonly string[] | undefined | null): number[] | null {
  if (!ids || ids.length === 0) return null;
  const numeric = ids.map((id) => Number(id)).filter((n) => Number.isFinite(n));
  return numeric.length ? numeric : null;
}

/** Normalize reports lookup payloads (raw array or `{ data: [...] }` envelope). */
export function extractReportLookupRows<T extends Record<string, unknown>>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (!payload || typeof payload !== 'object') return [];
  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) return record.data as T[];
  if (Array.isArray(record.content)) return record.content as T[];
  if (Array.isArray(record.result)) return record.result as T[];
  if (Array.isArray(record.results)) return record.results as T[];
  return [];
}

export function withReportDateRange(
  body: Record<string, unknown>,
  dateFrom: string,
  dateTo: string,
  keys: { from: string; to: string } = { from: 'from_date', to: 'to_date' },
): Record<string, unknown> {
  const from = toIsoDateStart(dateFrom);
  const to = toIsoDateEnd(dateTo);
  body[keys.from] = from ?? null;
  body[keys.to] = to ?? null;
  return body;
}

export async function exportReportBlob(
  path: string,
  body: Record<string, unknown>,
): Promise<ApiResult<ReportExportFile>> {
  const res = await reportsApiExportRequest(path, body);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}

export function downloadReportBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function toCount(value: number | string | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

export function toLabel(value: string | string[] | null | undefined): string {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return (value ?? '').trim();
}

export function displayReportCell(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  if (!t || t === '-') return '—';
  return t;
}

/** e.g. "Mr. Nimali Silva (IT Division)" when division is present. */
export function formatOfficerWithDivision(
  officer: string | null | undefined,
  division: string | null | undefined,
): string {
  const name = displayReportCell(officer);
  const div = (division ?? '').trim();
  if (!div || div === '-' || name === '—') return name;
  return `${name} (${div})`;
}

/** IANA timezone for report export APIs (e.g. Asia/Colombo). */
export function reportClientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function withReportTimeZone(body: Record<string, unknown>): Record<string, unknown> {
  body.time_zone = reportClientTimeZone();
  return body;
}
